import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, ShieldCheck } from 'lucide-react';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-white text-gray-600 pt-12 pb-8 border-t border-gray-100 font-sans">
      <div className="max-w-7xl mx-auto px-6">
        {/* Top Section: 4-Column Grid with generous breathing room */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-10">
          {/* Column 1: Brand Info & Primary CTA */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=240,quality=90,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/logo2_3.png"
                alt="Thai Hot Chili Logo"
                className="h-20 w-auto object-contain"
              />
            </div>

            {/* Primary CTA Area with clear spacing above */}
            <div className="flex items-center gap-3 mt-2">
              <Link
                to="/kontakt"
                className="px-6 py-3 lg:px-4 lg:py-2.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs lg:text-[10px] font-bold tracking-widest uppercase rounded-full transition-all duration-300 shadow-lg shadow-brand-primary/20 shrink-0"
              >
                BOOK FOODTRUCK
              </Link>
              <a
                href="tel:+4552901306"
                className="p-3 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-full transition-all duration-300 shadow-lg shadow-brand-primary/20 flex items-center justify-center"
                aria-label="Ring til foodtruck"
              >
                <Phone className="w-4 h-4" />
              </a>
            </div>

            {/* Smiley Badge in Column 1 */}
            <div className="mt-2">
              <a
                href="https://www.findsmiley.dk/Sider/Search.aspx?k=thai%20hot%20chili"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-100 rounded-xl transition-all duration-300 group"
              >
                <img
                  src="https://assets.auxodev.dk/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/smiley_5.png"
                  alt="Fødevarestyrelsen Smiley Rapport"
                  className="h-7 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                />
                <div className="flex flex-col text-left">
                  <span className="text-[9px] text-gray-400 font-bold tracking-wider uppercase leading-none">FØDEVARESTYRELSEN</span>
                  <span className="text-[11px] text-brand-dark font-bold tracking-wide uppercase mt-0.5 leading-none">SMILEY-RAPPORT</span>
                </div>
              </a>
            </div>
          </div>

          {/* Column 2: Åbningstider */}
          <div className="flex flex-col gap-4">
            <h3 className="text-xs font-bold tracking-widest text-brand-dark uppercase">
              ÅBNINGSTIDER
            </h3>
            <div className="flex flex-col gap-4 text-sm">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-brand-dark shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-brand-dark uppercase tracking-wider text-xs">TAKE-AWAY RESTAURANT</p>
                  <p className="text-gray-500 mt-0.5 text-xs">Bymidten 11, Skibby</p>
                  <p className="text-brand-primary font-bold mt-1 text-xs uppercase tracking-wide">VINTERÅBNINGSTID:</p>
                  <p className="text-brand-dark font-semibold text-xs">MAN - FREDAG: 16.00 - 20.00</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-brand-dark shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-brand-dark uppercase tracking-wider text-xs">FOODTRUCK & CATERING</p>
                  <p className="text-gray-500 mt-0.5 text-xs">Efter aftale til arrangementer i hele landet.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Column 3: Kontakt os */}
          <div className="flex flex-col gap-4">
            <h3 className="text-xs font-bold tracking-widest text-brand-dark uppercase">
              KONTAKT OS
            </h3>
            <div className="flex flex-col gap-3 text-sm">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-brand-dark shrink-0 mt-0.5" />
                <span className="text-gray-600 text-xs uppercase tracking-wider">
                  Bymidten 11, 4050 Skibby
                </span>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-brand-dark shrink-0 mt-0.5" />
                <div className="flex flex-col">
                  <span className="text-gray-400 text-[10px] uppercase tracking-widest">Take-away Skibby</span>
                  <a href="tel:+4529832959" className="text-brand-dark hover:text-brand-primary transition-colors font-semibold text-sm">
                    29 83 29 59
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-brand-dark shrink-0 mt-0.5" />
                <div className="flex flex-col">
                  <span className="text-gray-400 text-[10px] uppercase tracking-widest">Foodtruck & Booking</span>
                  <a href="tel:+4552901306" className="text-brand-dark hover:text-brand-primary transition-colors font-semibold text-sm">
                    52 90 13 06
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-brand-dark shrink-0 mt-0.5" />
                <div className="flex flex-col">
                  <span className="text-gray-400 text-[10px] uppercase tracking-widest">E-mail</span>
                  <a href="mailto:jes.valente@gmail.com" className="text-brand-dark hover:text-brand-primary transition-colors font-semibold text-sm break-all">
                    jes.valente@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Column 4: Genveje */}
          <div className="flex flex-col gap-4">
            <h3 className="text-xs font-bold tracking-widest text-brand-dark uppercase">
              GENVEJE
            </h3>
            <nav className="flex flex-col gap-2.5 text-sm">
              <Link to="/" className="text-gray-600 hover:text-brand-primary transition-colors uppercase tracking-wider text-xs">Forside</Link>
              <Link to="/menukort" className="text-gray-600 hover:text-brand-primary transition-colors uppercase tracking-wider text-xs">Menukort</Link>
              <Link to="/foodtruck-catering" className="text-gray-600 hover:text-brand-primary transition-colors uppercase tracking-wider text-xs">Foodtruck & Catering</Link>
              <Link to="/galleri" className="text-gray-600 hover:text-brand-primary transition-colors uppercase tracking-wider text-xs">Galleri</Link>
              <Link to="/om-os" className="text-gray-600 hover:text-brand-primary transition-colors uppercase tracking-wider text-xs">Om Os</Link>
              <Link to="/kontakt" className="text-gray-600 hover:text-brand-primary transition-colors uppercase tracking-wider text-xs">Kontakt</Link>
            </nav>
          </div>
        </div>

        {/* Divider line */}
        <div className="border-t border-gray-100"></div>

        {/* Bottom Section: Spacious single horizontal row */}
        <div className="pt-10 flex flex-col md:flex-row items-center justify-between gap-8">
          {/* Left: Copyright */}
          <div className="text-[11px] text-gray-400 tracking-wider uppercase">
            © {currentYear} THAI HOT CHILI. ALLE RETTIGHEDER FORBEHOLDES.
          </div>

          {/* Center-Right: Legal / Footer Links */}
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8 text-[11px] text-gray-400 tracking-wider">
            <span className="uppercase">CVR: 31521483</span>
            <span className="hidden sm:inline text-gray-200">•</span>
            <a 
              href="https://www.iubenda.com/privacy-policy/35387447" 
              className="iubenda-nostyle iubenda-noiframe iubenda-embed hover:text-brand-primary transition-colors uppercase" 
              title="Privatlivspolitik"
            >
              Privatlivspolitik
            </a>
            <span className="hidden sm:inline text-gray-200">•</span>
            <a 
              href="https://www.iubenda.com/privacy-policy/35387447/cookie-policy" 
              className="iubenda-nostyle iubenda-noiframe iubenda-embed hover:text-brand-primary transition-colors uppercase" 
              title="Cookiepolitik"
            >
              Cookiepolitik
            </a>
            <span className="hidden sm:inline text-gray-200">•</span>
            <span className="uppercase">UDVIKLET AF ET PROFESSIONELT DANSK WEBBUREAU</span>
            <span className="hidden sm:inline text-gray-200">•</span>
            <a 
              href="https://www.findsmiley.dk/Sider/Search.aspx?k=thai%20hot%20chili" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-brand-primary transition-colors uppercase text-gray-500"
            >
              <span>SMILEY-RAPPORT</span>
              <img
                src="https://assets.auxodev.dk/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/smiley_5.png"
                alt="Fødevarestyrelsen Smiley Rapport"
                className="h-6 w-auto object-contain rounded bg-white px-1 py-0.5 border border-gray-100"
              />
            </a>
          </div>

          {/* Right: Social Media Icons */}
          <div className="flex items-center gap-4">
            <a
              href="https://www.facebook.com/thaichiliaps/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2.5 bg-gray-50 hover:bg-brand-primary hover:text-white text-gray-600 rounded-full transition-all duration-300 border border-gray-100"
              aria-label="Følg os på Facebook"
            >
              <Facebook className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
